import { PageHeader } from "@/components/PageHeader";
import { LeadSection } from "@/components/LeadSection";
import { Section, Eyebrow, CTA } from "@/components/kit";
import { Reveal } from "@/components/motion";
import { DuoBanner } from "@/components/DuoBanner";
import { TextMarquee } from "@/components/TextMarquee";
import { Palette, Utensils, Gamepad2, Music, Star, Sparkles } from "lucide-react";
import { IMG } from "@/data/content";

const PROFILES = [
  {
    name: "Bella",
    full: "Isabella Arroyo",
    img: IMG.bellaPortrait,
    color: "#FF8A80",
    tag: "La actriz",
    desc: "Isabella la actriz y lleva más de la mitad de su vida frente a cámaras, en escenarios y en consultorios de ballet. Creció en Metepec en una familia con vocación artística y desde pequeña aprendió que la disciplina y el talento no se oponen, se multiplican. Es actriz profesional, bailarina de ballet y tap, y jugadora de tennis de nivel competitivo. Pero lo que la define más que sus logros es cómo se mueve por el mundo: con presencia, con intención y con una elegancia que no necesita esfuerzo. Su lema es: Todo se puede, si lo ensayas lo suficiente.",
    traits: [
      { icon: Music, t: "Ballet, Tap dance y tenis" },
      { icon: Star, t: "Actriz profesional y escritora de historias" },
      { icon: Sparkles, t: "Impecable y elegante" },
    ],
  },
  {
    name: "Anto",
    full: "Antonella",
    img: IMG.antoPortrait,
    color: "#81D4FA",
    tag: "La Soñadora",
    desc: "Antonella es espontaneidad pura, mirada viva y una energía que no se puede fingir. Caótica en el mejor sentido. Diseña, cocina, ama los videojuegos y pinta espectacular. Hace tonterías, caras y muecas: genera los momentos más espontáneos y virales del programa. Su superpoder es encontrar algo interesante en cualquier situación, incluidas las peores. Genera los momentos más virales del programa sin proponérselo, porque no actúa: simplemente reacciona. Con Bella forman el par más complementario del mundo: donde Bella pone espíritu, Anto pone chispa.Su lema es: Si no sale bien, al menos sale divertido.",
    traits: [
      { icon: Palette, t: "Diseño gráfico y pintura" },
      { icon: Utensils, t: "Cocina creativa" },
      { icon: Gamepad2, t: "Gamer de corazón y Cara de meme" },
    ],
  },
];

export default function AntoBella() {
  return (
    <>
      <PageHeader
        eyebrow="El Dúo · Anto & Bella"
        eyebrowColor="#E8C547"
        title={["Polos opuestos", "que se necesitan."]}
        subtitle="Anto y Bella son completamente distintas y se complementan perfecto. Una trae la estructura, la otra trae el caos. Y juntas hacen que viajar se sienta como salir con dos amigas muy diferentes. Juntas convierten cada viaje en algo que se siente real, cercano y divertido."
      />

      <Section className="!pt-6">
        <div className="grid gap-8 lg:grid-cols-2">
          {PROFILES.map((p, i) => (
            <Reveal key={p.name} delay={i * 0.1}>
              <div className="overflow-hidden rounded-[2.5rem] bg-white shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
                <div
                  className="relative h-72 overflow-hidden"
                  style={{ backgroundColor: p.color + "22" }}
                >
                  <img
                    src={p.img}
                    alt={p.full}
                    className="h-full w-full object-cover object-top"
                  />
                  <span
                    className="absolute left-6 top-6 rounded-full px-4 py-1.5 font-mono text-xs font-bold uppercase tracking-widest text-white"
                    style={{ backgroundColor: p.color }}
                  >
                    {p.tag}
                  </span>
                </div>
                <div className="p-8">
                  <h2 className="font-display text-5xl font-extrabold tracking-tighter text-ink">
                    {p.name}
                  </h2>
                  <p className="font-mono text-xs uppercase tracking-widest text-ink/40">
                    {p.full}
                  </p>
                  <p className="mt-4 text-ink/70 leading-relaxed">{p.desc}</p>
                  <div className="mt-6 grid gap-3">
                    {p.traits.map((t) => (
                      <div
                        key={t.t}
                        className="flex items-center gap-3 rounded-2xl bg-mist px-4 py-3"
                      >
                        <t.icon className="h-5 w-5" style={{ color: p.color }} />
                        <span className="font-medium text-ink">{t.t}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      <DuoBanner
        image={IMG.duoHero}
        imgSide="right"
        tint="#A8E6CF"
        eyebrowColor="#FF8A80"
        title="Juntas son otra cosa."
        text="Son dos chicas reales que a veces se sacan de quicio y a veces se salvan la vida. Y eso, frente a cámara, es irresistible. Bella prepara el terreno. Anto lo rompe. Bella pregunta con elegancia. Anto hace la cara que todos querían hacer. Juntas encuentran lo que ninguna encontraría sola."
        cta={{ to: "/episodios", label: "Verlas en acción" }}
      />

      <TextMarquee items={["Elegancia", "Chispa", "Curiosidad", "Amistad real"]} />

      <Section alt className="text-center">
        <Reveal>
          <Eyebrow color="#FF8A80" className="justify-center">
            Química real
          </Eyebrow>
          <h2 className="mx-auto mt-4 max-w-2xl font-display text-4xl sm:text-5xl font-extrabold leading-none tracking-tighter text-ink">
            Nada de pose. Solo dos amigas descubriendo el mundo. Lo que una no tiene, lo trae la otra
          </h2>
          <CTA to="/episodios" variant="coral" className="mt-8">
            Verlas en acción
          </CTA>
        </Reveal>
      </Section>
      <LeadSection path="/anto-bella" alt />
    </>
  );
}
